import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import TablePagination from '@mui/material/TablePagination';
import { useApi } from '../api/api';
import DispatchFilters from '../components/dispatch/DispatchFilters';
import DispatchRunsTable from '../components/dispatch/DispatchRunsTable';
import type { DispatchRun, DispatchRunsResponse } from '../types/dispatch';

const DEFAULT_FILTERS = {
  start: '',
  end: '',
  status: '',
  ticket_id: '',
  assignee: '',
};

const DispatchRunsPage: React.FC = () => {
  const { fetchWithAuth } = useApi();
  const fetchWithAuthRef = useRef(fetchWithAuth);
  fetchWithAuthRef.current = fetchWithAuth;

  const [runs, setRuns] = useState<DispatchRun[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [boards, setBoards] = useState<{ cw_id: number; name: string }[]>([]);

  const formatDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Fetch boards once on mount
  useEffect(() => {
    let cancelled = false;
    const doFetch = async () => {
      try {
        const res = await fetchWithAuthRef.current('/api/boards?page=1');
        if (cancelled) return;
        const boardsList = res.boards || [];
        setBoards(
          boardsList.map((b: { id: number; name: string }) => ({
            cw_id: b.id,
            name: b.name,
          }))
        );
      } catch {
        if (!cancelled) setBoards([]);
      }
    };
    doFetch();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch runs when filters, page, or rowsPerPage change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (filters.start) params.set('start', filters.start + 'T00:00:00Z');
    if (filters.end) params.set('end', filters.end + 'T23:59:59Z');
    if (filters.status) params.set('status', filters.status);
    if (filters.ticket_id) params.set('ticket_id', filters.ticket_id);
    if (filters.assignee) params.set('assignee', filters.assignee);
    params.set('limit', String(rowsPerPage));
    params.set('offset', String(page * rowsPerPage));

    fetchWithAuthRef.current(`/dispatch/runs?${params.toString()}`)
      .then((res: DispatchRunsResponse) => {
        if (cancelled) return;
        setRuns(res.runs || []);
        setTotal(res.total || 0);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load runs');
          setRuns([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters, page, rowsPerPage]);

  const handleFilterChange = (key: string, value: string | boolean | null) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleReset = () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    setFilters({
      ...DEFAULT_FILTERS,
      start: formatDate(weekAgo),
      end: formatDate(today),
    });
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    setFilters((prev) => ({
      ...prev,
      start: prev.start || formatDate(weekAgo),
      end: prev.end || formatDate(today),
    }));
  }, []);

  const boardsMap: Record<number, string> = boards.reduce(
    (acc, b) => ({ ...acc, [b.cw_id]: b.name }),
    {}
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        AI Dispatch Control Center
      </Typography>

      <DispatchFilters
        filters={filters}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DispatchRunsTable runs={runs} boardsMap={boardsMap} loading={loading} />
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Container>
  );
};

export default DispatchRunsPage;
